import {
  AuthContext,
  clearBonusCode,
  setBonusCode,
  useApi,
} from '@repo-level/core-front';
import { REASON_FOR_UPDATING_USER } from '@repo-level/core-front/contexts/auth/reducer';
import { BonusStage } from 'api/common';

import { ModalsContext, ModalType } from 'contexts/modal';
import { RealTimeServiceContext } from 'contexts/realTimeService';
import { groupMemberActiveBonus } from 'helpers/user';
import { useCallback, useContext, useMemo } from 'react';

export const useHandleBonus = (code: string | null | undefined) => {
  const { user, pendingUser, updateUser } = useContext(AuthContext);
  const { handleModal } = useContext(ModalsContext);

  const [requestsSetBonusCode, { pending: pendingSetBonusCode }] =
    useApi(setBonusCode);
  const [requestClearBonusCode, { pending: pendingClearBonusCode }] =
    useApi(clearBonusCode);

  const handleBonus = useCallback(async () => {
    if (user?.canIssueBonuses) {
      const requestApi = code
        ? requestsSetBonusCode({
            depositBonusCode: code,
          })
        : requestClearBonusCode();

      const { error } = await requestApi.request;

      if (error != null) return;

      updateUser({ depositBonusCode: code }, REASON_FOR_UPDATING_USER.DEPOSIT);
    }

    handleModal(ModalType.Deposit)();
  }, [
    handleModal,
    user?.canIssueBonuses,
    code,
    updateUser,
    requestsSetBonusCode,
    requestClearBonusCode,
  ]);

  return {
    handleBonus,
    pendingBonus: pendingSetBonusCode || pendingClearBonusCode || pendingUser,
  };
};

export const useUserWageringBonuses = () => {
  const { user } = useContext(AuthContext);
  const { bonuses } = useContext(RealTimeServiceContext);

  const groupWageringBonus = useMemo(
    () => groupMemberActiveBonus(user),
    [user]
  );

  const activeWageringBonus = useMemo(() => {
    if (!user) return;
    return bonuses.find(({ stage }) => stage === BonusStage.HandleBets);
  }, [bonuses, user]);

  return {
    groupWageringBonus,
    activeWageringBonus,
    hasWageringBonus: !!(groupWageringBonus || activeWageringBonus),
  };
};

export const useUserCanIssueBonuses = () => {
  const { user, pendingUser } = useContext(AuthContext);

  const isUserCanIssueBonuses = useMemo(
    () => !!user?.canIssueBonuses && !pendingUser,
    [user, pendingUser]
  );

  return isUserCanIssueBonuses;
};
